/**
 * Wire protocol shared by server and client. JSON messages over a WebSocket.
 *
 * Every state change produces a full `room` snapshot for each connection,
 * tailored to that connection's seat (so hidden cards never leave the server).
 * Snapshots are small (a few KB) so we don't bother with diffs.
 */

import type { Seat } from './cards.js';
import type { GameAction } from './game.js';
import type { RulesConfig } from './rules.js';
import type { PlayerView } from './view.js';

export const PROTOCOL_VERSION = 1;

export const EMOTES = ['👍', '👏', '😂', '😮', '😬', '🔥', '🤝', '🙈'] as const;
export type Emote = (typeof EMOTES)[number];

export const LIMITS = {
  nameMax: 20,
  chatMax: 200,
  chatHistory: 60,
  maxSpectators: 12,
  maxMessageBytes: 4096,
} as const;

export type ClientMessage =
  | { type: 'hello'; token?: string; name?: string; protocol?: number }
  | { type: 'set_name'; name: string }
  | { type: 'create_room'; rules?: Partial<RulesConfig> }
  | { type: 'join_room'; code: string; spectate?: boolean }
  | { type: 'leave_room' }
  | { type: 'sit'; seat: Seat }
  | { type: 'stand' }
  | { type: 'add_bot'; seat: Seat }
  | { type: 'remove_bot'; seat: Seat }
  | { type: 'set_ready'; ready: boolean }
  | { type: 'set_rules'; rules: Partial<RulesConfig> }
  | { type: 'start_game' }
  | { type: 'game_action'; action: GameAction }
  | { type: 'chat'; text: string }
  | { type: 'emote'; emote: Emote }
  | { type: 'new_game' }
  /** "I'm here": stop the stepped-away countdown, or take your seat back from the bot covering it. */
  | { type: 'im_here' }
  /** A tester's note. `context` is optional client detail (screen size, settings), kept short by the server. */
  | { type: 'feedback'; text: string; context?: Record<string, unknown> }
  | { type: 'ping' };

export interface HumanOccupant {
  kind: 'human';
  playerId: string;
  name: string;
  connected: boolean;
  ready: boolean;
  /** Epoch ms when a bot will take over for a disconnected player (game in progress only). */
  botTakeoverAt: number | null;
  /** True while a bot is playing this seat on the human's behalf. */
  botControlled: boolean;
  /** Epoch ms when a bot will take over for a player who has gone quiet on their turn (the warning countdown). */
  idleTakeoverAt?: number | null;
  /** While a bot covers this seat: epoch ms until which the player can take it back. After that the bot keeps it. */
  returnBy?: number | null;
}

export interface BotOccupant {
  kind: 'bot';
  name: string;
}

export interface SeatInfo {
  seat: Seat;
  occupant: HumanOccupant | BotOccupant | null;
}

export interface ChatEntry {
  id: number;
  at: number;
  /** null for system messages. */
  from: { playerId: string; name: string; seat: Seat | null } | null;
  text: string;
  /** "vegas": the Vegas dealer heckling a bad play. Players on Plain dealer talk don't see these. */
  tone?: 'vegas';
}

export interface RoomSnapshot {
  code: string;
  hostId: string;
  status: 'lobby' | 'playing' | 'finished';
  rules: RulesConfig;
  seats: SeatInfo[];
  spectators: { playerId: string; name: string; connected: boolean }[];
  you: { playerId: string; seat: Seat | null; isHost: boolean; spectator: boolean };
  game: PlayerView | null;
  chat: ChatEntry[];
  /** Server clock, so clients can render countdowns despite clock skew. */
  serverTime: number;
  /** Games completed in this room: [team0 wins, team1 wins]. */
  gamesWon: [number, number];
}

export type ServerMessage =
  | { type: 'welcome'; token: string; playerId: string; name: string; protocol: number }
  | { type: 'room'; room: RoomSnapshot }
  | { type: 'left_room' }
  | { type: 'error'; message: string; code?: string }
  | { type: 'chat'; entry: ChatEntry }
  | { type: 'emote'; seat: Seat | null; name: string; emote: Emote }
  | { type: 'pong' }
  /** Your feedback note was saved. */
  | { type: 'feedback_ok' };

export const ROOM_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
export const ROOM_CODE_LENGTH = 5;

export function normalizeRoomCode(input: string): string {
  return input.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, ROOM_CODE_LENGTH);
}
